/**
 * Inspection Schema Service
 * API calls for managing versioned inspection question schemas
 */

import { api } from './api';
import type {
  InspectionSchemaCreate,
  InspectionSchemaDetailResponse,
  InspectionSchemaUpdate,
  InspectionFormPreviewResponse,
  PaginatedSchemasResponse,
  SchemaFilters,
  SchemaNewVersionCreate,
  SchemaQuestionCreate,
  SchemaQuestionUpdate,
  SchemaQuestion,
  SchemaStageCreate,
  SchemaStageQuestions,
  SchemaStageUpdate,
  SchemaVersionResponse,
  SpecialProjectSchemaCreate,
  SpecialProjectSchemaResponse,
  QuestionReorderRequest,
  StateCertification,
  StateCertificationCreate,
  StateCertificationListResponse,
  StateCertificationUpdate,
} from '../types/inspectionSchema';

const BASE_URL = '/inspection-schemas';

/**
 * List all inspection schemas with optional filtering
 */
export async function listSchemas(
  filters?: SchemaFilters
): Promise<PaginatedSchemasResponse> {
  const params = new URLSearchParams();

  if (filters?.project_type) {
    params.append('project_type', filters.project_type);
  }
  if (filters?.state_code) {
    params.append('state_code', filters.state_code);
  }
  if (filters?.permit_type) {
    params.append('permit_type', filters.permit_type);
  }
  if (filters?.include_inactive) {
    params.append('include_inactive', 'true');
  }
  if (filters?.include_locked) {
    params.append('include_locked', 'true');
  }
  if (filters?.page) {
    params.append('page', filters.page.toString());
  }
  if (filters?.size) {
    params.append('size', filters.size.toString());
  }

  const queryString = params.toString();
  const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;

  const response = await api.get<PaginatedSchemasResponse>(url);
  return response.data;
}

/**
 * Get a single schema with all stages and questions
 */
export async function getSchema(
  schemaId: string
): Promise<InspectionSchemaDetailResponse> {
  const response = await api.get<InspectionSchemaDetailResponse>(
    `${BASE_URL}/${schemaId}`
  );
  return response.data;
}

/**
 * Get version history for a schema lineage
 */
export async function getSchemaVersions(
  schemaId: string
): Promise<SchemaVersionResponse[]> {
  const response = await api.get<SchemaVersionResponse[]>(
    `${BASE_URL}/${schemaId}/versions`
  );
  return response.data;
}

/**
 * Resolve the applicable schema for a given site
 */
export async function resolveSchemaForSite(
  siteId: string
): Promise<InspectionSchemaDetailResponse> {
  const response = await api.get<InspectionSchemaDetailResponse>(
    `${BASE_URL}/resolve?site_id=${siteId}`
  );
  return response.data;
}

/**
 * Create a new inspection schema
 */
export async function createSchema(
  data: InspectionSchemaCreate
): Promise<InspectionSchemaDetailResponse> {
  const response = await api.post<InspectionSchemaDetailResponse>(
    BASE_URL,
    data
  );
  return response.data;
}

/**
 * Create a new version from existing schema
 */
export async function createNewVersion(
  schemaId: string,
  data: SchemaNewVersionCreate
): Promise<InspectionSchemaDetailResponse> {
  const response = await api.post<InspectionSchemaDetailResponse>(
    `${BASE_URL}/${schemaId}/new-version`,
    data
  );
  return response.data;
}

/**
 * Update schema metadata (not questions)
 */
export async function updateSchema(
  schemaId: string,
  data: InspectionSchemaUpdate
): Promise<InspectionSchemaDetailResponse> {
  const response = await api.put<InspectionSchemaDetailResponse>(
    `${BASE_URL}/${schemaId}`,
    data
  );
  return response.data;
}

/**
 * Delete (soft-delete) a schema
 */
export async function deleteSchema(
  schemaId: string
): Promise<{ message: string; id: string }> {
  const response = await api.delete<{ message: string; id: string }>(
    `${BASE_URL}/${schemaId}`
  );
  return response.data;
}

/**
 * Lock a schema so it stops appearing in the site-detail picker. Super_admin only.
 */
export async function lockSchema(
  schemaId: string
): Promise<InspectionSchemaDetailResponse> {
  const response = await api.post<InspectionSchemaDetailResponse>(
    `${BASE_URL}/${schemaId}/lock`
  );
  return response.data;
}

/**
 * Unlock a schema so it reappears in the site-detail picker. Super_admin only.
 */
export async function unlockSchema(
  schemaId: string
): Promise<InspectionSchemaDetailResponse> {
  const response = await api.post<InspectionSchemaDetailResponse>(
    `${BASE_URL}/${schemaId}/unlock`
  );
  return response.data;
}

// ============================================================================
// Stage Operations
// ============================================================================

/**
 * Add a stage section to a schema
 */
export async function addStage(
  schemaId: string,
  data: SchemaStageCreate
): Promise<SchemaStageQuestions> {
  const response = await api.post<SchemaStageQuestions>(
    `${BASE_URL}/${schemaId}/stages`,
    data
  );
  return response.data;
}

/**
 * Update a stage section
 */
export async function updateStage(
  schemaId: string,
  stageId: string,
  data: SchemaStageUpdate
): Promise<SchemaStageQuestions> {
  const response = await api.put<SchemaStageQuestions>(
    `${BASE_URL}/${schemaId}/stages/${stageId}`,
    data
  );
  return response.data;
}

/**
 * Delete a stage and its questions
 */
export async function deleteStage(
  schemaId: string,
  stageId: string
): Promise<{ message: string; id: string }> {
  const response = await api.delete<{ message: string; id: string }>(
    `${BASE_URL}/${schemaId}/stages/${stageId}`
  );
  return response.data;
}

/**
 * Reorder stages within a schema
 */
export async function reorderStages(
  schemaId: string,
  stageIds: string[]
): Promise<{ message: string; count: number }> {
  const response = await api.put<{ message: string; count: number }>(
    `${BASE_URL}/${schemaId}/stages/reorder`,
    { stage_ids: stageIds }
  );
  return response.data;
}

// ============================================================================
// Question Operations
// ============================================================================

/**
 * Add a question to a stage
 */
export async function addQuestion(
  schemaId: string,
  stageId: string,
  data: SchemaQuestionCreate
): Promise<SchemaQuestion> {
  const response = await api.post<SchemaQuestion>(
    `${BASE_URL}/${schemaId}/stages/${stageId}/questions`,
    data
  );
  return response.data;
}

/**
 * Update a question
 */
export async function updateQuestion(
  schemaId: string,
  stageId: string,
  questionId: string,
  data: SchemaQuestionUpdate
): Promise<SchemaQuestion> {
  const response = await api.put<SchemaQuestion>(
    `${BASE_URL}/${schemaId}/stages/${stageId}/questions/${questionId}`,
    data
  );
  return response.data;
}

/**
 * Delete a question
 */
export async function deleteQuestion(
  schemaId: string,
  stageId: string,
  questionId: string
): Promise<{ message: string; id: string }> {
  const response = await api.delete<{ message: string; id: string }>(
    `${BASE_URL}/${schemaId}/stages/${stageId}/questions/${questionId}`
  );
  return response.data;
}

/**
 * Reorder questions within a stage
 */
export async function reorderQuestions(
  schemaId: string,
  stageId: string,
  data: QuestionReorderRequest
): Promise<{ message: string; count: number }> {
  const response = await api.put<{ message: string; count: number }>(
    `${BASE_URL}/${schemaId}/stages/${stageId}/questions/reorder`,
    data
  );
  return response.data;
}

// ============================================================================
// Schema-Level Question Operations (no stage required)
// ============================================================================

/**
 * Reorder schema-level questions (Jim 2026-08-28: "same capability we built
 * for the stages"). Order of ``questionIds`` becomes ``sort_order``.
 */
export async function reorderSchemaQuestions(
  schemaId: string,
  questionIds: string[]
): Promise<{ message: string; count: number }> {
  const response = await api.put<{ message: string; count: number }>(
    `${BASE_URL}/${schemaId}/questions/reorder`,
    { question_ids: questionIds }
  );
  return response.data;
}

/**
 * Add a question directly to a schema (not tied to any stage)
 */
export async function addSchemaQuestion(
  schemaId: string,
  data: SchemaQuestionCreate
): Promise<SchemaQuestion> {
  const response = await api.post<SchemaQuestion>(
    `${BASE_URL}/${schemaId}/questions`,
    data
  );
  return response.data;
}

/**
 * Update a schema-level question
 */
export async function updateSchemaQuestion(
  schemaId: string,
  questionId: string,
  data: SchemaQuestionUpdate
): Promise<SchemaQuestion> {
  const response = await api.put<SchemaQuestion>(
    `${BASE_URL}/${schemaId}/questions/${questionId}`,
    data
  );
  return response.data;
}

/**
 * Delete a schema-level question
 */
export async function deleteSchemaQuestion(
  schemaId: string,
  questionId: string
): Promise<{ message: string; id: string }> {
  const response = await api.delete<{ message: string; id: string }>(
    `${BASE_URL}/${schemaId}/questions/${questionId}`
  );
  return response.data;
}

// ============================================================================
// Preview
// ============================================================================

/**
 * Preview inspection form based on schema
 */
export async function previewSchema(
  schemaId: string,
  constructionStageId?: string
): Promise<InspectionFormPreviewResponse> {
  const params = constructionStageId
    ? `?construction_stage_id=${constructionStageId}`
    : '';
  const response = await api.get<InspectionFormPreviewResponse>(
    `${BASE_URL}/${schemaId}/preview${params}`
  );
  return response.data;
}

// ============================================================================
// Special Projects
// ============================================================================

/**
 * Create a special project override for a site
 */
export async function createSpecialProject(
  data: SpecialProjectSchemaCreate
): Promise<SpecialProjectSchemaResponse> {
  const response = await api.post<SpecialProjectSchemaResponse>(
    `${BASE_URL}/special-projects`,
    data
  );
  return response.data;
}

/**
 * Delete a special project override
 */
export async function deleteSpecialProject(
  siteId: string
): Promise<{ message: string; site_id: string }> {
  const response = await api.delete<{ message: string; site_id: string }>(
    `${BASE_URL}/special-projects/${siteId}`
  );
  return response.data;
}

// ============================================================================
// State Certifications
// ============================================================================

/**
 * List all state certifications
 */
export async function listCertifications(
  search?: string
): Promise<StateCertificationListResponse> {
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  const response = await api.get<StateCertificationListResponse>(
    `${BASE_URL}/certifications${params}`
  );
  return response.data;
}

/**
 * Get certification for a specific state
 */
export async function getCertification(
  stateCode: string
): Promise<StateCertification> {
  const response = await api.get<StateCertification>(
    `${BASE_URL}/certifications/${stateCode}`
  );
  return response.data;
}

/**
 * Create a new state certification
 */
export async function createCertification(
  data: StateCertificationCreate
): Promise<StateCertification> {
  const response = await api.post<StateCertification>(
    `${BASE_URL}/certifications`,
    data
  );
  return response.data;
}

/**
 * Update a state certification
 */
export async function updateCertification(
  stateCode: string,
  data: StateCertificationUpdate
): Promise<StateCertification> {
  const response = await api.put<StateCertification>(
    `${BASE_URL}/certifications/${stateCode}`,
    data
  );
  return response.data;
}

/**
 * Delete a state certification
 */
export async function deleteCertification(
  stateCode: string
): Promise<{ message: string; state_code: string }> {
  const response = await api.delete<{ message: string; state_code: string }>(
    `${BASE_URL}/certifications/${stateCode}`
  );
  return response.data;
}

// Export service object for convenience
export const inspectionSchemaService = {
  listSchemas,
  getSchema,
  getSchemaVersions,
  resolveSchemaForSite,
  createSchema,
  createNewVersion,
  updateSchema,
  deleteSchema,
  lockSchema,
  unlockSchema,
  addStage,
  updateStage,
  deleteStage,
  reorderStages,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  reorderQuestions,
  reorderSchemaQuestions,
  addSchemaQuestion,
  updateSchemaQuestion,
  deleteSchemaQuestion,
  previewSchema,
  createSpecialProject,
  deleteSpecialProject,
  // Certifications
  listCertifications,
  getCertification,
  createCertification,
  updateCertification,
  deleteCertification,
};
